/* eslint-disable max-lines */
import EventClass from "common/utils/ui/event-class";
import CompositionEstimatesClass from "components/jiffy/products/composition-estimates-class";
import { updateTimeleftDisplay, formatDeliveryDateText } from "common/utils/delivery/dtf-delivery";
import { setupTileHeightEqualization } from "./tile-height-equalizer";
import DtfUploadManager from "./dtf-upload-manager";
import DtfTileUI from "./dtf-tile-ui";
import DtfFileProcessor from "./dtf-file-processor";
import "./popular-dtf-transfers-section.scss";

const fetcher = new CompositionEstimatesClass();

// Global upload manager instance
let uploadManager = null;

class DtfTileUpload extends EventClass {
  constructor($container) {
    super({
      eventSelector: `[data-tile-id="${$container.dataset.tileId}"] .js-file-input`,
      event: "change"
    });

    this.$container = $container;
    this.tileId = $container.dataset.tileId;

    this.uiInstance = new DtfTileUI($container, uploadManager);
    this.fileProcessor = new DtfFileProcessor($container, this.uiInstance, uploadManager);

    uploadManager.registerTile(this);
  }

  event(e) {
    e.preventDefault();
    const [file] = e.target.files;
    if (!file) return;

    e.target.value = "";

    if (this.fileProcessor.handleFileSelection(file)) {
      this.fileProcessor.uploadArtwork();
    }
  }

  setDisabled(disabled) {
    this.uiInstance.setDisabled(disabled);
  }
}

/**
 * Updates delivery styling based on timing
 */
const updateDeliveryStyles = ($deliveryContainer, isToday, isTomorrow) => {
  // Toggle fast-delivery class for today and tomorrow deliveries (shows zap icon)
  $deliveryContainer.classList.toggle("fast-delivery", isToday || isTomorrow);

  // Toggle blue pill styling for today and tomorrow deliveries
  const deliveryElement = $deliveryContainer.closest(
    ".jiffytransfers-landing-v2-popular-dtf-transfers-section__delivery"
  );
  if (deliveryElement) {
    deliveryElement.classList.toggle(
      "jiffytransfers-landing-v2-popular-dtf-transfers-section__delivery--blue-pill",
      isToday || isTomorrow
    );
  }
};

/**
 * Renders a single DTF delivery container
 */
const renderSingleDtfContainer = ($container, estimate) => {
  const $spinner = $container.querySelector(".js-plp-delivery-spinner");
  const $deliveryContainer = $container.querySelector(".js-plp-dates-container");
  const $deliveryDate = $container.querySelector(".js-plp-delivery-date");
  const $timeleft = $container.querySelector(".js-plp-delivery-timeleft");

  $spinner?.classList.add("hidden");

  if (!$deliveryContainer || !$deliveryDate || !estimate?.date) return;

  const {
    date,
    delivery_time: deliveryTime,
    delivery_time_end: deliveryTimeEnd,
    timeleft
  } = estimate;
  const isToday = date.toLowerCase().includes("today");
  const isTomorrow = date.toLowerCase().includes("tomorrow");

  $deliveryDate.textContent = formatDeliveryDateText(date, {
    deliveryTime,
    deliveryTimeEnd,
    includeByForTimeRange: false
  });
  updateDeliveryStyles($deliveryContainer, isToday, isTomorrow);
  updateTimeleftDisplay($timeleft, isToday, timeleft);

  $deliveryContainer.classList.remove("hidden");
};

/**
 * Renders delivery estimates for DTF landing cards
 */
const renderDtfDeliveryEstimates = (compositionId, estimate) => {
  document
    .querySelectorAll(`.js-dtf-landing-delivery[data-composition-id="${compositionId}"]`)
    .forEach($container => renderSingleDtfContainer($container, estimate));
};

/**
 * Loads delivery estimates for DTF tiles
 */
const loadDeliveryEstimates = () => {
  const $deliverySections = document.querySelectorAll(
    ".js-dtf-landing-delivery[data-composition-id]"
  );

  const compositionIds = Array.from($deliverySections)
    .map($section => $section.dataset.compositionId)
    .filter(Boolean);

  if (compositionIds.length > 0) {
    fetcher
      .fetch(compositionIds)
      .then(([data]) => {
        if (data.success && data.estimates) {
          compositionIds.forEach(compositionId => {
            const estimate = data.estimates[compositionId];
            if (estimate?.date) {
              renderDtfDeliveryEstimates(compositionId, estimate);
            }
          });
        }

        setupTileHeightEqualization();
      })
      .catch(error => {
        window.Honeybadger?.notify("Error loading delivery estimates for DTF product card", {
          context: {
            compositionIds,
            errorMessage: error?.message
          }
        });
      });
  }
};

export default () => {
  // Initialize the global upload manager
  if (!uploadManager) {
    uploadManager = new DtfUploadManager();
  }

  const $tiles = document.querySelectorAll(".js-dtf-upload-tile");
  const tileInstances = [];
  $tiles.forEach($tile => {
    const tileUpload = new DtfTileUpload($tile);
    tileInstances.push(tileUpload);
  });

  loadDeliveryEstimates();

  // Set up tile height equalization
  // This speacially added to support safari's older versions
  setupTileHeightEqualization();

  return tileInstances;
};
